import { classNames, h } from "omi";

import { PricingSectionCertificationsCta } from "@/components/modals/modalTypes/paywallModal/certificationsCta.tsx";
import { NWButton } from "@/components/atoms/nw-button";
import { NWIcon } from "@/components/atoms/nw-icon";
import { NWLoadingSpinner } from "@/components";
import { Store } from "@/store";

import { BillingPeriod, ProductPrice, ProductPriceName } from "./models";

type PricingSectionProps = ProductPrice & {
  onClick: () => void;
  isLoading?: boolean;
  recommended?: boolean;
  showPriceSubtext?: boolean;
  fetchingError?: string;
  billingPeriod: BillingPeriod;
};

export function PricingSection({
  name,
  price,
  description,
  cta,
  features,
  onClick,
  billingPeriod,
  recommended = false,
  showPriceSubtext = false,
  isLoading = false,
  fetchingError,
}: PricingSectionProps) {
  const isAnnalPlan = billingPeriod === BillingPeriod.ANNUAL && price.yearly !== price.monthly;
  const getSubtext = () => `USD P/M${isAnnalPlan ? ", billed annually" : ""}`;

  const renderCtaButton = () => {
    if (name === ProductPriceName.PRO_PLUS_CERTS) {
      return <PricingSectionCertificationsCta cta={cta} class={"w-full"} />;
    }

    return (
      <NWButton
        size="fit"
        type={recommended ? "primary" : "secondary"}
        onClick={onClick}
        loadingText={cta}
        disabled={!Store.features.hasFeature("projects.payments") && name !== ProductPriceName.FREE}
      >
        <span className="flex items-center gap-3 h-8">
          {cta}
          {isLoading ? (
            <NWLoadingSpinner size="xs" color="white" padding="none" />
          ) : (
            <NWIcon name="arrow-right" width={20} height={20} />
          )}
        </span>
      </NWButton>
    );
  };

  return (
    <div
      className={classNames("flex flex-col h-full py-12 px-8 rounded-3xl relative", {
        "bg-gray-50 rounded-tl-none": recommended,
      })}
    >
      {recommended && (
        <div className="flex items-center gap-2 bg-black px-4 py-2 justify-center rounded-t-lg absolute top-0 -translate-y-full left-0">
          <p className="text-sm font-semibold text-white">Recommended</p>
        </div>
      )}

      <div className="flex flex-col flex-1">
        <div className="space-y-10">
          <div className="flex flex-col gap-2">
            <p className="text-xl text-primary">{name}</p>
            <p className="text-3xl font-semibold text-primary">
              {price[billingPeriod]}
              {showPriceSubtext && <span className="ml-2 text-base font-normal text-tertiary">{getSubtext()}</span>}
            </p>
            <p className="text-sm text-tertiary self-stretch">{description}</p>
          </div>
          <div className="flex flex-col">
            <ul className="list-disc list-inside text-sm text-tertiary self-stretch space-y-4">
              {features.map(feature => (
                <li className="flex items-start gap-2 border-b border-gray-200 pb-2">
                  <NWIcon className="text-black" name="tick" />
                  <p>{feature}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-auto pt-8 w-full">
          {renderCtaButton()}
          {fetchingError ? <p className="text-error-400">{fetchingError}</p> : <div></div>}
        </div>
      </div>
    </div>
  );
}
